import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, ExternalLink, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const EpisodesSection = ({ onOpenVideo }) => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        const response = await fetch('/api/episodes');
        const data = await response.json();
        setEpisodes(data.episodes || []);
      } catch (err) {
        // Silent — page still works without episodes list
      } finally {
        setLoading(false);
      }
    };
    fetchEpisodes();
  }, []);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <section id="episodes" className="section-spacing section-sage-mist">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <p className="label-uppercase mb-4">Latest Episodes</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-normal mb-6">
            Stories Worth Hearing
          </h2>
          <p className="text-lg text-muted-foreground max-w-[65ch] mx-auto leading-relaxed mb-8">
            Raw, reflective conversations with people who left everything to start over.
          </p>
          <Button 
            variant="outline" 
            className="rounded-full border-2"
            onClick={() => window.open('https://www.youtube.com/@ALifeElsewhere', '_blank')}
            data-testid="youtube-subscribe-button"
          >
            Subscribe on YouTube
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading episodes...</p>
          </div>
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {episodes.map((episode) => (
              <motion.div 
                key={episode.id} 
                variants={item}
                className={episode.featured ? 'sm:col-span-2' : ''}
              >
                <Card className="overflow-hidden group cursor-pointer h-full shadow-warm hover-lift" onClick={() => onOpenVideo(episode.id)}>
                  <div className="relative aspect-video overflow-hidden">
                    <img 
                      src={`https://img.youtube.com/vi/${episode.id}/maxresdefault.jpg`}
                      alt={episode.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 rounded-full btn-premium flex items-center justify-center shadow-2xl">
                        <Play className="w-7 h-7 text-white ml-1" fill="white" />
                      </div>
                    </div>
                    {episode.featured && (
                      <Badge className="absolute top-4 left-4 pulse-glow">Featured</Badge>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3 text-xs font-mono text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>{episode.duration}</span>
                      <span>•</span>
                      <span>{episode.views} views</span>
                    </div>
                    <h3 className="font-normal text-xl mb-2 line-clamp-2 leading-tight">
                      {episode.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                      {episode.description}
                    </p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default EpisodesSection;